"use client";

/**
 * The ONE diff vocabulary, used identically in an expanded history row and in
 * the restore confirmation — so the confirmation shows the user exactly what
 * they just read.
 *
 * Visual semantics (plan → "Visual semantics for the diff"):
 *
 *   Removed / previous value  `−` prefix, strikethrough, red-tinted
 *   Added / new value         `+` prefix, green-tinted
 *   `system`-class field      dimmed, "Calculated" chip, no restore control
 *   Non-revertable field      the reason, spelled out
 *   Empty → value             renders "(empty)", never a blank cell
 *
 * Colour is NEVER the only signal: every side carries its `+`/`−` glyph AND a
 * visible "Removed"/"Added" text label, so the diff survives colour-blindness,
 * a greyscale print, and a screen reader.
 */

import React from "react";
import type { NonRevertableReason } from "@wtc/audit/types";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { AuditChangeDto } from "@/lib/audit/api-contract";

/** Shown when a value is absent — never an empty cell. */
export const EMPTY_VALUE_TEXT = "(empty)";

/**
 * Plain-language reason a recorded field still cannot be written back. Keyed by
 * the contract's `NonRevertableReason` so a new reason code is a type error
 * here rather than a blank explanation in the UI.
 */
export const NON_REVERTABLE_REASON_TEXT: Record<NonRevertableReason, string> = {
  derived: "Calculated automatically — restoring it would just be recomputed.",
  "unique-constrained":
    "Only one record can own this value, so restoring it could collide with whoever holds it now.",
  "foreign-key": "The record this pointed at may no longer exist.",
  secret: "Stored securely and never written back.",
};

export function nonRevertableReasonText(reason: NonRevertableReason | undefined): string {
  return reason
    ? NON_REVERTABLE_REASON_TEXT[reason]
    : "This field cannot be restored.";
}

interface DiffValueProps {
  kind: "removed" | "added";
  display: string | null;
  /** `system`-class fields are de-emphasized wherever they appear. */
  dimmed?: boolean;
}

/**
 * One side of a before/after pair. The glyph, the word, and the colour all say
 * the same thing; any one of them alone is enough.
 */
function DiffValue({ kind, display, dimmed = false }: DiffValueProps) {
  const removed = kind === "removed";
  const isEmpty = display === null || display === "";
  return (
    <div
      data-testid={`audit-diff-${kind}`}
      className={cn(
        "flex items-start gap-2 rounded-md border px-2 py-1 text-sm",
        removed
          ? "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40"
          : "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40",
        dimmed && "opacity-70"
      )}
    >
      <span aria-hidden="true" className="font-mono font-semibold leading-5">
        {removed ? "−" : "+"}
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 break-words leading-5",
          removed && "line-through",
          isEmpty && "italic text-muted-foreground"
        )}
      >
        {isEmpty ? EMPTY_VALUE_TEXT : display}
      </span>
      <span className="shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
        {removed ? "Removed" : "Added"}
      </span>
    </div>
  );
}

interface ElementBreakdownProps {
  label: "Added" | "Removed" | "Changed";
  items: string[];
}

function ElementBreakdown({ label, items }: ElementBreakdownProps) {
  if (items.length === 0) return null;
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={`${label}-${item}`} className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{label}</span> {item}
        </li>
      ))}
    </ul>
  );
}

export interface AuditFieldDiffProps {
  change: AuditChangeDto;
}

/** Per-field before/after for one change inside an expanded history row. */
export function AuditFieldDiff({ change }: AuditFieldDiffProps) {
  const isSystem = change.fieldClass === "system";

  return (
    <div
      data-testid="audit-field-diff"
      className={cn("space-y-1.5 rounded-lg border border-border p-3", isSystem && "opacity-75")}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn("text-sm font-medium", isSystem && "text-muted-foreground")}>
          {change.label}
        </span>
        {isSystem && (
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
            Calculated
          </Badge>
        )}
      </div>

      <DiffValue kind="removed" display={change.oldDisplay} dimmed={isSystem} />
      <DiffValue kind="added" display={change.newDisplay} dimmed={isSystem} />

      {change.elements && (
        <div className="space-y-0.5 pt-0.5">
          <ElementBreakdown label="Added" items={change.elements.added} />
          <ElementBreakdown label="Removed" items={change.elements.removed} />
          <ElementBreakdown label="Changed" items={change.elements.changed} />
        </div>
      )}

      {!change.revertable && (
        <p data-testid="audit-non-revertable-reason" className="text-xs text-muted-foreground">
          Cannot be restored — {nonRevertableReasonText(change.nonRevertableReason)}
        </p>
      )}
    </div>
  );
}
